import type { StaffImportPreview, StaffImportUploadTarget } from "@lafayette/schemas";
import { useCallback, useReducer, useRef } from "react";

import { describeImportError } from "../errors";
import { useImportRequest } from "../queries";
import {
  checkImportFile,
  extensionOf,
  IDLE_UPLOAD,
  retryStage,
  uploadReducer,
} from "../upload-machine";

export type ReadFile = Readonly<{
  preview: StaffImportPreview;
  objectPath: string;
  fileName: string;
}>;

/**
 * Runs the upload machine: W1 (a signed target), the direct PUT to storage, then P1 (the server reads
 * the stored file). The browser sends the file and parses nothing. A result that arrives after the
 * staff member chose another file or started over is dropped.
 */
export function useUploadFlow(onRead: (read: ReadFile) => void) {
  const request = useImportRequest();
  const [state, dispatch] = useReducer(uploadReducer, IDLE_UPLOAD);
  const file = useRef<File | null>(null);
  const run = useRef(0);

  const execute = useCallback(
    async (chosen: File, from: "requesting" | "reading", objectPathIn: string | null) => {
      run.current += 1;
      const id = run.current;
      const current = () => run.current === id;
      let objectPath = objectPathIn;
      try {
        if (from === "requesting") {
          const extension = extensionOf(chosen.name);
          if (extension === null) return;
          const target = await request<StaffImportUploadTarget>(
            { command: "createUploadTarget" },
            { fileName: chosen.name, byteSize: chosen.size, extension },
          );
          if (!current()) return;
          objectPath = target.objectPath;
          dispatch({ type: "target_received", objectPath });
          await request<void>({ command: "uploadFile" }, { target, body: chosen });
          if (!current()) return;
          dispatch({ type: "uploaded" });
        }
        const preview = await request<StaffImportPreview>(
          { command: "previewFile" },
          { objectPath: objectPath as string },
        );
        if (!current()) return;
        dispatch({ type: "read" });
        onRead({ preview, objectPath: objectPath as string, fileName: chosen.name });
      } catch (error) {
        if (!current()) return;
        const failure = describeImportError(error);
        dispatch({
          type: "failed",
          message: failure.message,
          retryable: failure.retryable,
          parseErrors: failure.parseErrors,
        });
      }
    },
    [onRead, request],
  );

  const start = useCallback(
    (chosen: File) => {
      file.current = chosen;
      // A result of an earlier file must not land on this one.
      run.current += 1;
      dispatch({ type: "choose", file: chosen });
      if (checkImportFile(chosen).ok) void execute(chosen, "requesting", null);
    },
    [execute],
  );

  const retry = useCallback(() => {
    const chosen = file.current;
    const stage = retryStage(state);
    if (chosen === null || stage === null || state.status !== "failed") return;
    dispatch({ type: "retry" });
    void execute(chosen, stage === "reading" ? "reading" : "requesting", state.objectPath);
  }, [execute, state]);

  const reset = useCallback(() => {
    run.current += 1;
    file.current = null;
    dispatch({ type: "reset" });
  }, []);

  return { state, start, retry, reset };
}
